import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { SnMapClusterPoint } from '@lib/api';
import { SnAlarm, SnCamera, SnUserSettings } from '@lib/api/entities';
import { SnAlarmsAPI, SnCamerasAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { SnLiveTrackingService } from '@lib/live-tracking/services';

import {
  AlarmMapQueryParams,
  AlarmQueryParams,
  AlarmSelectionEvent,
  CameraLayerObject,
  PopupRequestEvent,
} from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class AlarmsService implements OnDestroy {
  public alarmDialogs: MatDialogRef<unknown>[] = [];

  private _alarms$ = new BehaviorSubject<SnAlarm[]>([]);
  private _clusteredAlarms$ = new BehaviorSubject<SnMapClusterPoint[]>([]);
  private _selectedAlarm$ = new Subject<AlarmSelectionEvent>();
  private _viewAlarmDetails$ = new Subject<SnAlarm>();
  private _requestPopupOpen$ = new Subject<PopupRequestEvent>();
  private _requestNavigation$ = new Subject<{ alarm: SnAlarm; coordinates?: [number, number] }>();
  private readonly _destroy$ = new Subject<void>();
  private _selected: SnAlarm | null = null;
  private _alertClustering: boolean = false;
  private _fetchTrigger$ = new Subject<AlarmQueryParams>();

  // Business logic properties
  private _isSlewing$ = new BehaviorSubject<boolean>(false);
  private _alertClustering$ = new BehaviorSubject<boolean>(true);
  private _isToggled$ = new BehaviorSubject<boolean>(false);
  private _settings$ = new BehaviorSubject<SnUserSettings | null>(null);

  get alarms(): SnAlarm[] {
    return this._alarms$.value;
  }

  get alarms$(): Observable<SnAlarm[]> {
    return this._alarms$.asObservable();
  }

  get clusteredAlarms(): SnMapClusterPoint[] {
    return this._clusteredAlarms$.value;
  }

  get clusteredAlarms$(): Observable<SnMapClusterPoint[]> {
    return this._clusteredAlarms$.asObservable();
  }

  // Business logic property observables
  get isSlewing$(): Observable<boolean> {
    return this._isSlewing$.asObservable();
  }

  get alertClustering$(): Observable<boolean> {
    return this._alertClustering$.asObservable();
  }

  get isToggled$(): Observable<boolean> {
    return this._isToggled$.asObservable();
  }

  get settings$(): Observable<SnUserSettings | null> {
    return this._settings$.asObservable();
  }

  get selectedAlarm$(): Observable<AlarmSelectionEvent> {
    return this._selectedAlarm$.asObservable();
  }

  get selectedAlarm(): SnAlarm | null {
    return this._selected;
  }

  get viewAlarmDetails$(): Observable<SnAlarm> {
    return this._viewAlarmDetails$.asObservable();
  }

  get requestPopupOpen$(): Observable<PopupRequestEvent> {
    return this._requestPopupOpen$.asObservable();
  }

  get requestNavigation$(): Observable<{ alarm: SnAlarm; coordinates?: [number, number] }> {
    return this._requestNavigation$.asObservable();
  }

  get isAvailable(): boolean {
    return this._liveTrackingService.liveAlarmsIsAvailable;
  }

  get settings(): SnUserSettings {
    return this._currentOrganization.settings as unknown as SnUserSettings;
  }

  get isSlewing(): boolean {
    return this._isSlewing$.value;
  }

  get alertClustering(): boolean {
    return this._alertClustering$.value;
  }

  get isToggled(): boolean {
    return this._isToggled$.value;
  }

  get settingsValue(): SnUserSettings | null {
    return this._settings$.value;
  }

  constructor(
    protected readonly _alarmsAPI: SnAlarmsAPI,
    protected readonly _camerasAPI: SnCamerasAPI,
    protected readonly _liveTrackingService: SnLiveTrackingService,
    protected readonly _currentOrganization: SnCurrentOrganization,
    protected readonly _matDialog: MatDialog,
  ) {
    // Set up debounced fetch to prevent multiple rapid API calls
    this._fetchTrigger$
      .pipe(
        debounceTime(100), // Debounce rapid calls
        takeUntil(this._destroy$),
      )
      .subscribe((queryParams) => {
        this._fetchAlarmData(queryParams);
      });
  }

  public initAlarms(params: AlarmQueryParams): void {
    this._fetchTrigger$.next(params);
  }

  public updateMapBounds(mapQueryParams: Partial<AlarmMapQueryParams>): void {
    const currentParams: AlarmQueryParams = {
      mapQueryParams,
      filteredTypes: [],
      filteredReasons: [],
    };
    this._fetchTrigger$.next(currentParams);
  }

  // Business logic property setters
  public setIsSlewing(isSlewing: boolean): void {
    this._isSlewing$.next(isSlewing);
  }

  public setAlertClustering(enabled: boolean): void {
    this._alertClustering$.next(enabled);
  }

  public setIsToggled(isToggled: boolean): void {
    this._isToggled$.next(isToggled);
  }

  public setSettings(settings: SnUserSettings): void {
    this._settings$.next(settings);
  }

  public isSelected(alarmId: string): boolean {
    return this._selected ? this._selected.id === alarmId : false;
  }

  public selectAlarm(alarmId: string, action?: string): Observable<AlarmSelectionEvent> {
    return this._alarmsAPI.getAlarm(alarmId).pipe(
      map((alarm) => {
        const result = { alarm, cameras: [] as SnCamera[], action };
        this._selectedAlarm$.next(result);
        return result;
      }),
    );
  }

  public deselectAlarm(): void {
    this._selected = null;
  }

  public isSkydioAlarm(alarm: SnAlarm): boolean {
    return alarm?.type?.toLowerCase() === 'skydio';
  }

  public getCurrentLocationForAlarm(
    alarm: SnAlarm,
    cameraLayerObjects: CameraLayerObject[],
  ): [number, number] | null {
    if (!this.isSkydioAlarm(alarm)) {
      return null;
    }

    const cameraId = alarm.details?.cameraId;
    if (!cameraId || !cameraLayerObjects || cameraLayerObjects.length === 0) {
      return null;
    }

    try {
      const skydioData = cameraLayerObjects.find((obj) => obj.camera_id === cameraId);

      if (skydioData?.data?.geometry?.coordinates) {
        const coords = skydioData.data.geometry.coordinates;
        return [coords[0], coords[1]];
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  public ngOnDestroy(): void {
    this._viewAlarmDetails$.complete();
    this._selectedAlarm$.complete();
    this._requestPopupOpen$.complete();
    this._requestNavigation$.complete();
    this._destroy$.next();
    this._destroy$.complete();
    this._isToggled$.next(false);
  }

  public deselect(): void {
    if (this._selected) {
      this._selected = null;
    }
  }

  public closeAlarmPopupOnListener(alarm: SnAlarm): void {
    // This will be handled by the map service that subscribes to popup events
  }

  public select(
    id: string,
    action?: string,
    cameraLayerObjects?: CameraLayerObject[],
  ): Observable<AlarmSelectionEvent> {
    return this._alarmsAPI.getAlarm(id).pipe(
      map((alarm) => {
        this._selected = alarm;
        const result: AlarmSelectionEvent = { alarm, cameras: [] };

        switch (action) {
          case 'view':
            this._handleViewAction(alarm, cameraLayerObjects || []);
            break;
          case 'dock':
            this._handleDockAction(alarm, cameraLayerObjects || []);
            break;
          case 'showDialog':
            this._openDetailsWithoutCoordinates(alarm);
            break;
          default:
            this._handleDefaultAction(alarm, cameraLayerObjects || []);
            break;
        }

        return result;
      }),
      takeUntil(this._destroy$),
    );
  }

  public viewAlarmDetails(id: string): void {
    this._alarmsAPI
      .getAlarm(id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((alarm) => {
        this._viewAlarmDetails$.next(alarm);
      });
  }

  private _fetchAlarmData(queryParams: AlarmQueryParams): void {
    const hasMapBounds =
      queryParams.mapQueryParams?.pageMapLeftTop && queryParams.mapQueryParams?.pageMapRightBottom;

    if (!hasMapBounds) {
      return;
    }

    const params: AlarmMapQueryParams = {
      pageMapClustered: this._alertClustering,
      pageMapGridSize: 30,
      pageMapZoomLevel: 0,
      pageMapLeftTop: '',
      pageMapRightBottom: '',
      type_name__not_in: queryParams.filteredTypes.join(','),
      message__not_in: queryParams.filteredReasons.join(','),
      ...queryParams.mapQueryParams,
    };

    this._alarmsAPI
      .getAlarmMap(params)
      .pipe(
        map((response) => ({
          results: response.results,
          clusterResults: response.clusterResults || [],
        })),
        takeUntil(this._destroy$),
      )
      .subscribe(({ results, clusterResults }) => {
        this._alarms$.next(results);
        this._clusteredAlarms$.next(clusterResults);
      });
  }

  private _handleViewAction(alarm: SnAlarm, cameraLayerObjects: CameraLayerObject[]): void {
    if (this.isSkydioAlarm(alarm)) {
      const result = this._handleSkydioNavigation(alarm, cameraLayerObjects);
      this._selected = result.enhancedAlarm || alarm;

      if (result.enhancedAlarm) {
        const coords = this.getCurrentLocationForAlarm(alarm, cameraLayerObjects);
        this._requestPopupOpen$.next({ alarm: this._selected, coordinates: coords });
      } else {
        this._requestPopupOpen$.next({ alarm: this._selected });
      }

      const coords = result.enhancedAlarm?.geometry.coordinates as [number, number] | undefined;
      this._requestNavigation$.next({
        alarm: this._selected,
        coordinates: coords,
      });
    } else if (alarm.details?.layerData) {
      this._requestPopupOpen$.next({ alarm });
    } else {
      this._requestPopupOpen$.next({ alarm });
      this._requestNavigation$.next({ alarm });
    }
  }

  private _handleDockAction(alarm: SnAlarm, cameraLayerObjects: CameraLayerObject[]): void {
    const processed = this._processAlarmForAction(alarm, cameraLayerObjects);
    this._dockCamerasWithoutNavigation(processed.alarm, true, cameraLayerObjects);
  }

  private _handleDefaultAction(alarm: SnAlarm, cameraLayerObjects: CameraLayerObject[]): void {
    if (this.isSkydioAlarm(alarm)) {
      const result = this._handleSkydioNavigation(alarm, cameraLayerObjects);
      const alarmToUse = result.enhancedAlarm || alarm;
      this._selected = alarmToUse;
      this._getNearbyCamerasWithAction(alarmToUse, true, 'default', cameraLayerObjects);
    } else {
      this._selected = alarm;
      this._selectedAlarm$.next({ alarm: this._selected, cameras: [], action: 'default' });
      this._requestPopupOpen$.next({ alarm });
      this._requestNavigation$.next({ alarm });
    }
  }

  private _processAlarmForAction(
    alarm: SnAlarm,
    cameraLayerObjects: CameraLayerObject[],
  ): { alarm: SnAlarm; navigated: boolean } {
    if (this.isSkydioAlarm(alarm)) {
      const result = this._handleSkydioNavigation(alarm, cameraLayerObjects);
      return {
        alarm: result.enhancedAlarm || alarm,
        navigated: result.navigated,
      };
    }
    return { alarm, navigated: false };
  }

  private _handleSkydioNavigation(
    alarm: SnAlarm,
    cameraLayerObjects: CameraLayerObject[],
  ): { navigated: boolean; enhancedAlarm?: SnAlarm } {
    const cachedCurrentLocation = this.getCurrentLocationForAlarm(alarm, cameraLayerObjects);

    const coordinates = cachedCurrentLocation || (alarm.geometry.coordinates as [number, number]);
    const enhancedAlarm = cachedCurrentLocation
      ? this._createAlarmWithCachedLocation(alarm, cachedCurrentLocation)
      : undefined;

    this._requestNavigation$.next({ alarm, coordinates: coordinates as [number, number] });

    return { navigated: true, enhancedAlarm };
  }

  private _dockCamerasWithoutNavigation(
    alert: SnAlarm,
    openPopup?: boolean,
    cameraLayerObjects?: CameraLayerObject[],
  ): void {
    if (this.isSkydioAlarm(alert)) {
      const cameraId = alert.details?.cameraId;
      if (cameraId) {
        this._camerasAPI
          .getCamera(cameraId)
          .pipe(takeUntil(this._destroy$))
          .subscribe({
            next: (camera) => {
              this._selected = alert;
              this._selectedAlarm$.next({
                alarm: this._selected,
                cameras: [camera, ...camera.descendantCameras],
                action: 'dock',
              });
              if (openPopup) {
                this._requestPopupOpen$.next({ alarm: alert });
              }
            },
            error: () => {
              this._getNearbyCamerasWithAction(alert, openPopup, 'dock', cameraLayerObjects || []);
            },
          });
      } else {
        this._getNearbyCamerasWithAction(alert, openPopup, 'dock', cameraLayerObjects || []);
      }
    } else {
      this._getNearbyCamerasWithAction(alert, openPopup, 'dock', cameraLayerObjects || []);
    }
  }

  private _getNearbyCamerasWithAction(
    alarm: SnAlarm,
    openPopup?: boolean,
    action?: string,
    cameraLayerObjects?: CameraLayerObject[],
  ): void {
    const coords = alarm.geometry.coordinates;
    const radius = this._currentOrganization.settings.autoDockRadius || 0.1;
    const cameras = this._currentOrganization.settings.autoDockMaxCameras;
    this._camerasAPI
      .getNearCamera(coords[0], coords[1], radius, cameras)
      .pipe(takeUntil(this._destroy$))
      .subscribe((cams) => {
        this._selected = alarm;

        const payload: AlarmSelectionEvent = {
          alarm: this._selected,
          cameras: cams,
          ...(action && { action }),
        };

        this._selectedAlarm$.next(payload);

        if (openPopup) {
          this._requestPopupOpen$.next({ alarm: this._selected });
        }
      });
  }

  private _openDetailsWithoutCoordinates(alarm: SnAlarm): void {
    // This method will be handled by the map service
    // The business service emits popup request events instead
    this._requestPopupOpen$.next({ alarm });
  }

  private _createAlarmWithCachedLocation(
    originalAlarm: SnAlarm,
    cachedLocation: [number, number],
  ): SnAlarm {
    const alarmData = {
      ...originalAlarm,
      geometry: {
        ...originalAlarm.geometry,
        coordinates: cachedLocation,
      },
    };

    return new SnAlarm(alarmData);
  }
}
